import { Radio, Tooltip, Skeleton, Spin } from 'antd'
import { Icon } from '@iconify/react'
import classNames from 'classnames'
import { useDiagnosisFormat } from 'features/Procedure/hooks/useDiagnosisFormat'
import { useSkipConfirmation } from 'features/Procedure/hooks/useSkipConfirmation'
import type { ProcedureItem, SoapImage } from 'features/Procedure/state/procedure-api.type'
import { CameraCaptureIconButton } from './camera-capture-icon-button'
import styles from './procedure-image-list.module.scss'

type ProcedureItemRowProps = {
  procedure: ProcedureItem
  index: number
  isSelected: boolean
  images: SoapImage[]
  isDeletingImage: boolean
  isLoadingImage: boolean
  isUploadingPhoto?: boolean
  onSelect: () => void
  onDeleteImage: (imageId: number) => void
  onPreviewImage: (image: SoapImage) => void
  onUploadPhoto?: (procedure: ProcedureItem, dataUrl: string) => void
  disableCapture?: boolean
  disableActions?: boolean
}

export const ProcedureItemRow = ({
  procedure,
  index,
  isSelected,
  images,
  isDeletingImage,
  isLoadingImage,
  isUploadingPhoto,
  onSelect,
  onDeleteImage,
  onPreviewImage,
  onUploadPhoto,
  disableCapture = false,
  disableActions = false,
}: ProcedureItemRowProps) => {
  const { diagnosisFormat } = useDiagnosisFormat({ justifyCodes: procedure.justify_codes })
  const { executeWithConfirmation } = useSkipConfirmation()

  const icd10Codes = diagnosisFormat.map((d) => d.split('|')[1]).filter(Boolean)

  const handleDeleteClick = (imageId: number) => (e: React.MouseEvent) => {
    e.stopPropagation()
    executeWithConfirmation(
      {
        title: 'Delete Image',
        message: 'Are you sure you want to delete this image?',
        mainButtonText: 'Delete',
        secondButtonText: 'Cancel',
      },
      () => onDeleteImage(imageId),
    )
  }

  const handleImageClick = (image: SoapImage) => (e: React.MouseEvent) => {
    e.stopPropagation()
    onPreviewImage(image)
  }

  const handleCameraCapture = (dataUrl: string) => {
    onUploadPhoto?.(procedure, dataUrl)
  }

  const handleCameraContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const cameraCaptureButton = !disableActions ? (
    <div className={styles.cameraSlot} onClick={handleCameraContainerClick}>
      <CameraCaptureIconButton
        onCapture={handleCameraCapture}
        loading={isUploadingPhoto}
        disabled={disableCapture}
      />
    </div>
  ) : null

  return (
    <div
      className={classNames(styles.procedureItem, {
        [styles.selected]: isSelected && !disableActions,
      })}
      onClick={disableActions ? undefined : onSelect}
      style={disableActions ? { cursor: 'default' } : undefined}>
      <div className={styles.procedureHeader}>
        {!disableActions && <Radio checked={isSelected} className={styles.radio} />}
        <Tooltip
          title={`${procedure?.code_text} ${
            icd10Codes.length > 0 ? `ICD-10: ${icd10Codes.join(', ')}` : ''
          }`}
          className={styles.procedureInfo}>
          <span className={styles.procedureTitle}>{procedure?.code_text}</span>
          {icd10Codes.length > 0 && (
            <span className={styles.icdCodes}>ICD-10: {icd10Codes.join(', ')}</span>
          )}
        </Tooltip>
      </div>
      <div className={styles.imageContainer}>
        {isLoadingImage ? (
          <Skeleton.Button active className={styles.skeletonImage} />
        ) : images.length > 0 ? (
          <div className={styles.imagesGrid}>
            {images.map((image) => (
              <div
                key={image.id}
                className={classNames(styles.imageWrapper, {
                  [styles.imageWrapperUploading]: image.id < 0,
                })}>
                <Tooltip title={image.id < 0 ? 'Uploading image...' : 'Click to preview'}>
                  <button
                    type='button'
                    aria-label={`Preview procedure ${index + 1} image`}
                    className={styles.thumbnailButton}
                    disabled={image.id < 0}
                    onClick={handleImageClick(image)}
                    style={{ cursor: image.id < 0 ? 'progress' : 'pointer' }}>
                    <img
                      src={image.image}
                      alt={`Procedure ${index + 1}`}
                      className={styles.thumbnail}
                    />
                  </button>
                </Tooltip>
                {image.id < 0 && (
                  <div className={styles.uploadOverlay}>
                    <Spin size='small' />
                    <span>Uploading</span>
                  </div>
                )}
                {!disableActions && image.id > 0 && (
                  <Tooltip title='Delete image'>
                    <button
                      className={styles.deleteButton}
                      onClick={handleDeleteClick(image.id)}
                      disabled={isDeletingImage}>
                      <Icon icon='mdi:trash-can-outline' width={16} height={16} />
                    </button>
                  </Tooltip>
                )}
              </div>
            ))}
            {cameraCaptureButton}
          </div>
        ) : (
          <div className={styles.placeholder}>
            <Icon icon='mdi:image-outline' width={24} height={24} />
            <span>No image</span>
          </div>
        )}
        {images.length === 0 && cameraCaptureButton}
      </div>
    </div>
  )
}
